/**
 * QuestionnaireSummary.tsx — the deal drawer's read-only ANSWER SUMMARY
 * (2026-08-27 drawer restructure, CTO stage walkthrough).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * WHY: the Questionnaire tab used to render a GREYED-OUT replica of the
 * patient form for anything read-only (disabled upload buttons, grey
 * checkboxes) — "showing the 2 buttons is not good enough… hard to see what
 * they submitted". This replaces every read-only replica with the SAME
 * structured Q/A presentation the Review tab already renders
 * (StructuredQuestionnaire over the server's review sections — never raw
 * JSON), in `photoState` mode so the q14 photo question shows the ACTUAL
 * state (thumbnail / on-file note / "Patient chose: add before dispatch"),
 * never disabled buttons, and the q15 consent item keeps its tick-list. The
 * EDITABLE staff form (SinglePageForm) is untouched — it still serves the
 * clinician actually filling a draft; only the read-only replica died.
 *
 * The read is the SAME staff review endpoint the Review tab uses
 * (GET /v1/clinic/assessments/:id — served for drafts too: the projection
 * simply carries the answers recorded so far). One fetch per mount; a failed
 * read renders the retryable ErrorState inside StructuredQuestionnaire
 * (TA-06), and `renderItemExtra` threads the authorise flow's per-answer
 * note affordance through the summary.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { getAssessmentReview } from '../api/clinic';
import type { AssessmentReview } from '../api/types';
import { StructuredQuestionnaire } from '../clinicians/ReviewView';
import { DrawerSkeleton } from '../ui/skeletons';

export function QuestionnaireSummary({
  assessmentId,
  consentAgreedAt = null,
  renderItemExtra,
}: {
  assessmentId: string;
  /** The deal detail's consent timestamp — the quiet "Agreed by the patient"
   *  line under the consent tick-list; null/absent renders nothing (an older
   *  server must never read as "not agreed"). */
  consentAgreedAt?: string | null;
  /** Per-answer extra (the authorise flow's anchored-note control). */
  renderItemExtra?: (item: { questionKey: string; prompt: string }) => ReactNode;
}) {
  const [review, setReview] = useState<{ state: 'loading' | 'ready' | 'error'; data: AssessmentReview | null }>({
    state: 'loading',
    data: null,
  });

  const load = useCallback(() => {
    setReview({ state: 'loading', data: null });
    getAssessmentReview(assessmentId)
      .then((data) => setReview({ state: 'ready', data }))
      .catch(() => setReview({ state: 'error', data: null }));
  }, [assessmentId]);

  useEffect(() => {
    load();
  }, [load]);

  if (review.state === 'loading') return <DrawerSkeleton blocks={3} label="Loading the answers…" />;
  return (
    <StructuredQuestionnaire
      review={review}
      onRetry={load}
      consentAgreedAt={consentAgreedAt}
      photoState
      renderItemExtra={renderItemExtra}
    />
  );
}
